import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from '../../components/Sidebar';
import TopNav from '../../components/TopNav';
import { adminService, auditService } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { usePrompt } from '../../contexts/PromptContext';
import { Card, Badge, Button, Input, Field, Modal, Select, EmptyState, Spinner, Pagination } from '../../components/ui';
import useAuthStore from '../../store/authStore';
import { getApiErrorMessage } from '../../utils/apiErrors';

const ROLES = ['administrator', 'teacher', 'student', 'parent'];
const PAGE_SIZE = 20;
const RELATED_PAGE_SIZE = 10;
const ACTIVITY_PAGE_SIZE = 10;

const INITIAL_FILTERS = {
  name: '',
  email: '',
  status: '',
  verified: '',
  loginState: '',
  lastLoginAfter: '',
  lastLoginBefore: '',
};

function hasRole(user, role) {
  return user.roles?.some((item) => item.name === role);
}

function displayName(user, fallback) {
  return [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email || fallback;
}

function AdminUsers() {
  const { addNotification } = useNotification();
  const promptReason = usePrompt();
  const { user: currentUser } = useAuthStore();
  const { t, i18n } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [ordering, setOrdering] = useState('-created_at');
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [busyId, setBusyId] = useState(null);
  const [selected, setSelected] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetForm, setResetForm] = useState({ new_password: '', password_confirm: '', reason: '' });
  const [resetErrors, setResetErrors] = useState({});
  const [resetBusy, setResetBusy] = useState(false);
  const [relatedTarget, setRelatedTarget] = useState(null);
  const [relatedRows, setRelatedRows] = useState([]);
  const [relatedCount, setRelatedCount] = useState(0);
  const [relatedPage, setRelatedPage] = useState(1);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [activityTarget, setActivityTarget] = useState(null);
  const [activityRows, setActivityRows] = useState([]);
  const [activityCount, setActivityCount] = useState(0);
  const [activityPage, setActivityPage] = useState(1);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityFilters, setActivityFilters] = useState({ action: '', from: '', to: '' });
  const fileInputRef = useRef(null);

  const fetchUsers = useCallback(() => {
    setLoading(true);
    const params = { page, page_size: PAGE_SIZE, ordering };
    if (search) params.search = search;
    if (roleFilter) params.role = roleFilter;
    if (filters.name) params.name = filters.name;
    if (filters.email) params.email = filters.email;
    if (filters.status) params.is_active = filters.status;
    if (filters.verified) params.is_verified = filters.verified;
    if (filters.loginState) params.last_login_state = filters.loginState;
    if (filters.lastLoginAfter) params.last_login_after = filters.lastLoginAfter;
    if (filters.lastLoginBefore) params.last_login_before = filters.lastLoginBefore;
    adminService.getUsers(params)
      .then((res) => {
        const data = res.data;
        setUsers(Array.isArray(data) ? data : data.results || []);
        setCount(Array.isArray(data) ? data.length : data.count || 0);
        setSelected([]);
      })
      .catch(() => addNotification(t('Failed to load users'), 'error'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, roleFilter, filters, ordering, page]);

  useEffect(() => {
    const id = setTimeout(fetchUsers, 300);
    return () => clearTimeout(id);
  }, [fetchUsers]);

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setSearch('');
    setRoleFilter('');
    setFilters(INITIAL_FILTERS);
    setOrdering('-created_at');
    setPage(1);
  };

  const toggleOrdering = (field) => {
    setOrdering((current) => current === field ? `-${field}` : field);
    setPage(1);
  };

  const sortIndicator = (field) => {
    if (ordering === field) return '↑';
    if (ordering === `-${field}`) return '↓';
    return '↕';
  };

  useEffect(() => {
    if (!activityTarget) return undefined;
    let mounted = true;
    const id = setTimeout(() => {
      setActivityLoading(true);
      const params = {
        user: activityTarget.id,
        page: activityPage,
        page_size: ACTIVITY_PAGE_SIZE,
        ordering: '-created_at',
      };
      if (activityFilters.action) params.action = activityFilters.action;
      if (activityFilters.from) params.created_after = activityFilters.from;
      if (activityFilters.to) params.created_before = activityFilters.to;
      auditService.getEntries(params)
        .then((res) => {
          if (!mounted) return;
          const data = res.data;
          setActivityRows(Array.isArray(data) ? data : data.results || []);
          setActivityCount(Array.isArray(data) ? data.length : data.count || 0);
        })
        .catch(() => { if (mounted) addNotification(t('Failed to load user activity'), 'error'); })
        .finally(() => { if (mounted) setActivityLoading(false); });
    }, 300);
    return () => { mounted = false; clearTimeout(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityTarget, activityPage, activityFilters]);

  const openActivity = (target) => {
    setActivityTarget(target);
    setActivityRows([]);
    setActivityCount(0);
    setActivityPage(1);
    setActivityFilters({ action: '', from: '', to: '' });
  };

  const toggleActive = async (u) => {
    setBusyId(u.id);
    try {
      await adminService.updateUser(u.id, { is_active: !u.is_active });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, is_active: !x.is_active } : x)));
      addNotification(`${u.first_name} ${!u.is_active ? t('activated') : t('deactivated')}`, 'success');
    } catch (e) {
      addNotification(t('Failed to update user'), 'error');
    } finally {
      setBusyId(null);
    }
  };

  const removeUser = async (u) => {
    const reason = await promptReason(t('Delete {{name}}? This cannot be undone.', { name: `${u.first_name} ${u.last_name}` }));
    if (!reason) return;
    setBusyId(u.id);
    try {
      await adminService.deleteUser(u.id, reason);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
      setCount((current) => Math.max(0, current - 1));
      addNotification(t('User deleted'), 'success');
    } catch (e) {
      addNotification(getApiErrorMessage(e, t, 'Failed to delete user'), 'error');
    } finally {
      setBusyId(null);
    }
  };

  const toggleAdminRole = async (u) => {
    const isAdmin = u.roles?.some((r) => r.name === 'administrator');
    const reason = await promptReason(
      isAdmin
        ? t('Revoke administrator access from {{name}}?', { name: `${u.first_name} ${u.last_name}` })
        : t('Grant administrator access to {{name}}? They will be able to manage the entire platform.', { name: `${u.first_name} ${u.last_name}` })
    );
    if (!reason) return;
    setBusyId(u.id);
    try {
      const res = await adminService.setAdminRole(u.id, !isAdmin, reason);
      setUsers((prev) => prev.map((x) => (x.id === u.id ? res.data : x)));
      addNotification(isAdmin ? t('Administrator access revoked') : t('Administrator access granted'), 'success');
    } catch (e) {
      addNotification(getApiErrorMessage(e, t, 'Failed to update admin access'), 'error');
    } finally {
      setBusyId(null);
    }
  };

  const openPasswordReset = (u) => {
    setResetTarget(u);
    setResetForm({ new_password: '', password_confirm: '', reason: '' });
    setResetErrors({});
  };

  const closePasswordReset = () => {
    if (resetBusy) return;
    setResetTarget(null);
    setResetErrors({});
  };

  const submitPasswordReset = async (e) => {
    e.preventDefault();
    const errors = {};
    if (resetForm.new_password.length < 8) errors.new_password = t('Enter at least 8 characters');
    if (resetForm.new_password !== resetForm.password_confirm) errors.password_confirm = t('Passwords do not match');
    if (!resetForm.reason.trim()) errors.reason = t('Reason is required');
    if (Object.keys(errors).length) {
      setResetErrors(errors);
      return;
    }

    setResetBusy(true);
    setResetErrors({});
    try {
      await adminService.resetUserPassword(resetTarget.id, resetForm);
      addNotification(t('Password reset successfully'), 'success');
      setResetTarget(null);
    } catch (e) {
      const data = e.response?.data;
      if (data && typeof data === 'object') {
        const fieldErrors = {};
        Object.entries(data).forEach(([key, value]) => {
          fieldErrors[key] = Array.isArray(value) ? value.join(' ') : String(value);
        });
        setResetErrors(fieldErrors);
      } else {
        setResetErrors({ _error: t('Failed to reset password') });
      }
    } finally {
      setResetBusy(false);
    }
  };

  const allSelected = users.length > 0 && selected.length === users.length;
  const toggleSelectAll = () => setSelected(allSelected ? [] : users.map((u) => u.id));
  const toggleSelect = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleBulkAction = async (action) => {
    if (selected.length === 0) return;
    let reason = '';
    if (action === 'delete' || action === 'deactivate') {
      reason = await promptReason(
        action === 'delete'
          ? t('Delete {{count}} selected users? This cannot be undone.', { count: selected.length })
          : t('Deactivate {{count}} selected users?', { count: selected.length })
      );
      if (!reason) return;
    }
    setBulkBusy(true);
    try {
      const res = await adminService.bulkUpdateUsers(selected, action, reason);
      addNotification(t('{{count}} users updated', { count: res.data.affected }), 'success');
      fetchUsers();
    } catch (e) {
      addNotification(t('Action failed'), 'error');
    } finally {
      setBulkBusy(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = { ordering };
      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter;
      if (filters.name) params.name = filters.name;
      if (filters.email) params.email = filters.email;
      if (filters.status) params.is_active = filters.status;
      if (filters.verified) params.is_verified = filters.verified;
      if (filters.loginState) params.last_login_state = filters.loginState;
      if (filters.lastLoginAfter) params.last_login_after = filters.lastLoginAfter;
      if (filters.lastLoginBefore) params.last_login_before = filters.lastLoginBefore;
      const res = await adminService.exportUsersCsv(params);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'users.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      addNotification(t('Failed to export users'), 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const res = await adminService.importUsersCsv(file);
      const { created, skipped, errors } = res.data;
      addNotification(
        t('Import complete: {{created}} created, {{skipped}} skipped{{errorSuffix}}', {
          created, skipped,
          errorSuffix: errors.length ? t(', {{count}} errors', { count: errors.length }) : '',
        }),
        errors.length ? 'error' : 'success'
      );
      fetchUsers();
    } catch (e) {
      addNotification(getApiErrorMessage(e, t, 'Failed to import users'), 'error');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const loadRelated = async (target, nextPage = 1) => {
    setRelatedLoading(true);
    setRelatedPage(nextPage);
    try {
      const res = await adminService.getUserRelated(target.user.id, {
        type: target.type,
        page: nextPage,
        page_size: RELATED_PAGE_SIZE,
      });
      const data = res.data || {};
      setRelatedRows(Array.isArray(data) ? data : data.results || []);
      setRelatedCount(Array.isArray(data) ? data.length : data.count || 0);
    } catch (e) {
      addNotification(t('Failed to load related records'), 'error');
    } finally {
      setRelatedLoading(false);
    }
  };

  const openRelated = (user, type) => {
    const target = { user, type };
    setRelatedTarget(target);
    setRelatedRows([]);
    setRelatedCount(0);
    loadRelated(target, 1);
  };

  const closeRelated = () => {
    if (relatedLoading) return;
    setRelatedTarget(null);
    setRelatedRows([]);
  };

  return (
    <div className="flex h-screen bg-surface-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav title={t('User Management')} subtitle={t('View, activate, and manage every account')} />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div className="flex gap-2">
              {selected.length > 0 && (
                <>
                  <Button size="sm" variant="secondary" loading={bulkBusy} onClick={() => handleBulkAction('activate')}>
                    {t('Activate {{count}} selected', { count: selected.length })}
                  </Button>
                  <Button size="sm" variant="secondary" loading={bulkBusy} onClick={() => handleBulkAction('deactivate')}>
                    {t('Deactivate {{count}} selected', { count: selected.length })}
                  </Button>
                  <Button size="sm" variant="danger" loading={bulkBusy} onClick={() => handleBulkAction('delete')}>
                    {t('Delete {{count}} selected', { count: selected.length })}
                  </Button>
                </>
              )}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" loading={exporting} onClick={handleExport}>
                {t('Export CSV')}
              </Button>
              <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={handleImportFile} />
              <Button size="sm" variant="secondary" loading={importing} onClick={() => fileInputRef.current?.click()}>
                {t('Import CSV')}
              </Button>
            </div>
          </div>

          <Card padded={false} className="overflow-hidden">
            <div className="p-4 border-b border-gray-100 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-sm font-semibold text-gray-800">{t('Column Filters')}</h3>
                <Button size="sm" variant="secondary" onClick={clearFilters}>{t('Clear filters')}</Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
                <Field label={t('Name')}>
                  <Input
                    placeholder={t('Filter name...')}
                    value={filters.name}
                    onChange={(e) => updateFilter('name', e.target.value)}
                  />
                </Field>
                <Field label={t('Email')}>
                  <Input
                    type="email"
                    placeholder={t('Filter email...')}
                    value={filters.email}
                    onChange={(e) => updateFilter('email', e.target.value)}
                  />
                </Field>
                <Field label={t('Roles')}>
                  <Select value={roleFilter} onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}>
                    <option value="">{t('All roles')}</option>
                    {ROLES.map((r) => <option key={r} value={r}>{t(r)}</option>)}
                  </Select>
                </Field>
                <Field label={t('Status')}>
                  <Select value={filters.status} onChange={(e) => updateFilter('status', e.target.value)}>
                    <option value="">{t('All statuses')}</option>
                    <option value="true">{t('Active')}</option>
                    <option value="false">{t('Inactive')}</option>
                  </Select>
                </Field>
                <Field label={t('Verified')}>
                  <Select value={filters.verified} onChange={(e) => updateFilter('verified', e.target.value)}>
                    <option value="">{t('All verification states')}</option>
                    <option value="true">{t('Verified')}</option>
                    <option value="false">{t('Unverified')}</option>
                  </Select>
                </Field>
                <Field label={t('Login History')}>
                  <Select value={filters.loginState} onChange={(e) => updateFilter('loginState', e.target.value)}>
                    <option value="">{t('All login states')}</option>
                    <option value="has">{t('Has logged in')}</option>
                    <option value="never">{t('Never logged in')}</option>
                  </Select>
                </Field>
                <Field label={t('Global Search')}>
                  <Input
                    placeholder={t('Name or email...')}
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  />
                </Field>
                <Field label={t('Last login from')}>
                  <Input type="date" value={filters.lastLoginAfter} onChange={(e) => updateFilter('lastLoginAfter', e.target.value)} />
                </Field>
                <Field label={t('Last login to')}>
                  <Input type="date" value={filters.lastLoginBefore} onChange={(e) => updateFilter('lastLoginBefore', e.target.value)} />
                </Field>
              </div>
            </div>

            <Pagination page={page} count={count} pageSize={PAGE_SIZE} onPageChange={setPage} />

            {loading ? (
              <div className="p-12 flex justify-center"><Spinner /></div>
            ) : users.length === 0 ? (
              <EmptyState title={t('No users found')} description={t('Try adjusting your search or filter.')} />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                    <tr>
                      <th className="px-4 py-3 w-10">
                        <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} className="h-4 w-4 text-primary-600 border-gray-300 rounded" />
                      </th>
                      <th className="text-left px-4 py-3">
                        <button type="button" onClick={() => toggleOrdering('first_name')} className="inline-flex items-center gap-1 hover:text-primary-700">
                          {t('Name')} <span className={ordering.includes('first_name') ? 'text-primary-600' : 'text-gray-300'}>{sortIndicator('first_name')}</span>
                        </button>
                      </th>
                      <th className="text-left px-4 py-3">
                        <button type="button" onClick={() => toggleOrdering('email')} className="inline-flex items-center gap-1 hover:text-primary-700">
                          {t('Email')} <span className={ordering.includes('email') ? 'text-primary-600' : 'text-gray-300'}>{sortIndicator('email')}</span>
                        </button>
                      </th>
                      <th className="text-left px-4 py-3">{t('Roles')}</th>
                      <th className="text-left px-4 py-3">{t('Related')}</th>
                      <th className="text-left px-4 py-3">
                        <button type="button" onClick={() => toggleOrdering('is_active')} className="inline-flex items-center gap-1 hover:text-primary-700">
                          {t('Status')} <span className={ordering.includes('is_active') ? 'text-primary-600' : 'text-gray-300'}>{sortIndicator('is_active')}</span>
                        </button>
                      </th>
                      <th className="text-left px-4 py-3">
                        <button type="button" onClick={() => toggleOrdering('is_verified')} className="inline-flex items-center gap-1 hover:text-primary-700">
                          {t('Verified')} <span className={ordering.includes('is_verified') ? 'text-primary-600' : 'text-gray-300'}>{sortIndicator('is_verified')}</span>
                        </button>
                      </th>
                      <th className="text-left px-4 py-3">
                        <button type="button" onClick={() => toggleOrdering('last_login')} className="inline-flex items-center gap-1 hover:text-primary-700 whitespace-nowrap">
                          {t('Last Login')} <span className={ordering.includes('last_login') ? 'text-primary-600' : 'text-gray-300'}>{sortIndicator('last_login')}</span>
                        </button>
                      </th>
                      <th className="text-right px-4 py-3">{t('Actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {users.map((u) => (
                      <tr key={u.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selected.includes(u.id)}
                            onChange={() => toggleSelect(u.id)}
                            className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                          />
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900">{u.first_name} {u.last_name}</td>
                        <td className="px-4 py-3 text-gray-600">{u.email}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1 flex-wrap">
                            {(u.roles || []).map((r) => <Badge key={r.id} tone="blue">{t(r.name)}</Badge>)}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col items-start gap-1">
                            {hasRole(u, 'student') && (
                              <button type="button" onClick={() => openRelated(u, 'courses')} className="font-medium text-primary-700 hover:underline">
                                {t('{{count}} enrolled courses', { count: u.enrolled_courses_count || 0 })}
                              </button>
                            )}
                            {hasRole(u, 'parent') && (
                              <button type="button" onClick={() => openRelated(u, 'children')} className="font-medium text-primary-700 hover:underline">
                                {t('{{count}} linked students', { count: u.linked_students_count || 0 })}
                              </button>
                            )}
                            {!hasRole(u, 'student') && !hasRole(u, 'parent') && <span className="text-gray-400">—</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge tone={u.is_active ? 'green' : 'gray'}>{u.is_active ? t('Active') : t('Inactive')}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Badge tone={u.is_verified ? 'green' : 'yellow'}>{u.is_verified ? t('Verified') : t('Unverified')}</Badge>
                        </td>
                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                          {u.last_login ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(u.last_login)) : t('Never')}
                        </td>
                        <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                          <Button size="sm" variant="secondary" onClick={() => openActivity(u)}>
                            {t('View Activity')}
                          </Button>
                          <Button size="sm" variant="secondary" loading={busyId === u.id} onClick={() => toggleActive(u)}>
                            {u.is_active ? t('Deactivate') : t('Activate')}
                          </Button>
                          {u.id !== currentUser?.id && (
                            <Button size="sm" variant="secondary" loading={busyId === u.id} onClick={() => toggleAdminRole(u)}>
                              {(u.roles || []).some((r) => r.name === 'administrator') ? t('Revoke Admin') : t('Make Admin')}
                            </Button>
                          )}
                          <Button size="sm" variant="secondary" loading={busyId === u.id} onClick={() => openPasswordReset(u)}>
                            {t('Reset Password')}
                          </Button>
                          <Button size="sm" variant="danger" loading={busyId === u.id} onClick={() => removeUser(u)}>
                            {t('Delete')}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <Pagination page={page} count={count} pageSize={PAGE_SIZE} onPageChange={setPage} />
          </Card>

          <Modal
            open={!!relatedTarget}
            onClose={closeRelated}
            title={relatedTarget ? t(
              relatedTarget.type === 'courses' ? 'Enrolled courses for {{name}}' : 'Linked students for {{name}}',
              { name: displayName(relatedTarget.user, t('User')) },
            ) : ''}
            size="lg"
          >
            {relatedLoading ? (
              <div className="flex justify-center p-10"><Spinner /></div>
            ) : relatedRows.length === 0 ? (
              <EmptyState
                title={relatedTarget?.type === 'courses' ? t('No active course enrollments') : t('No approved linked students')}
                description={t('There are no related records to display.')}
              />
            ) : relatedTarget?.type === 'courses' ? (
              <div className="overflow-x-auto rounded-xl border border-gray-100">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-3 text-start">{t('Course')}</th>
                      <th className="px-4 py-3 text-start">{t('Grade')}</th>
                      <th className="px-4 py-3 text-start">{t('Start date')}</th>
                      <th className="px-4 py-3 text-start">{t('Access ends')}</th>
                      <th className="px-4 py-3 text-start">{t('Status')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {relatedRows.map((row) => (
                      <tr key={row.id}>
                        <td className="px-4 py-3 font-medium text-gray-900">{row.module_name}</td>
                        <td className="px-4 py-3 text-gray-600">{row.grade_name || '—'}</td>
                        <td className="px-4 py-3 text-gray-600">{row.start_date ? new Intl.DateTimeFormat(i18n.language).format(new Date(row.start_date)) : '—'}</td>
                        <td className="px-4 py-3 text-gray-600">{row.end_date ? new Intl.DateTimeFormat(i18n.language).format(new Date(row.end_date)) : '—'}</td>
                        <td className="px-4 py-3"><Badge status={row.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-gray-100">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-3 text-start">{t('Student')}</th>
                      <th className="px-4 py-3 text-start">{t('Email')}</th>
                      <th className="px-4 py-3 text-start">{t('Phone')}</th>
                      <th className="px-4 py-3 text-start">{t('Relationship')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {relatedRows.map((row) => (
                      <tr key={row.id}>
                        <td className="px-4 py-3 font-medium text-gray-900">{displayName(row.student, t('Student'))}</td>
                        <td className="px-4 py-3 text-gray-600">{row.student.email}</td>
                        <td className="px-4 py-3 text-gray-600">{row.student.phone || '—'}</td>
                        <td className="px-4 py-3 text-gray-600">{row.relationship || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {!relatedLoading && (
              <Pagination
                page={relatedPage}
                count={relatedCount}
                pageSize={RELATED_PAGE_SIZE}
                onPageChange={(nextPage) => loadRelated(relatedTarget, nextPage)}
              />
            )}
          </Modal>

          <Modal
            open={!!activityTarget}
            onClose={() => setActivityTarget(null)}
            title={activityTarget ? t('Activity for {{name}}', { name: displayName(activityTarget, t('User')) }) : ''}
            size="xl"
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-500">
                {t('Shows actions performed by this user and administrative actions performed directly on their account.')}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Field label={t('Action')}>
                  <Input
                    placeholder={t('Filter by action...')}
                    value={activityFilters.action}
                    onChange={(e) => { setActivityFilters((current) => ({ ...current, action: e.target.value })); setActivityPage(1); }}
                  />
                </Field>
                <Field label={t('From date')}>
                  <Input
                    type="date"
                    value={activityFilters.from}
                    onChange={(e) => { setActivityFilters((current) => ({ ...current, from: e.target.value })); setActivityPage(1); }}
                  />
                </Field>
                <Field label={t('To date')}>
                  <Input
                    type="date"
                    value={activityFilters.to}
                    onChange={(e) => { setActivityFilters((current) => ({ ...current, to: e.target.value })); setActivityPage(1); }}
                  />
                </Field>
              </div>
              {activityLoading ? (
                <div className="flex justify-center p-10"><Spinner /></div>
              ) : activityRows.length === 0 ? (
                <EmptyState title={t('No user activity found')} description={t('Try adjusting the activity filters.')} />
              ) : (
                <div className="overflow-x-auto rounded-xl border border-gray-100">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                      <tr>
                        <th className="px-4 py-3 text-start">{t('When')}</th>
                        <th className="px-4 py-3 text-start">{t('Actor')}</th>
                        <th className="px-4 py-3 text-start">{t('Action')}</th>
                        <th className="px-4 py-3 text-start">{t('Target')}</th>
                        <th className="px-4 py-3 text-start">{t('Details')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {activityRows.map((entry) => (
                        <tr key={entry.id} className="align-top">
                          <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                            {new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(entry.created_at))}
                          </td>
                          <td className="px-4 py-3 text-gray-700">{entry.actor ? displayName(entry.actor, t('User')) : t('System')}</td>
                          <td className="px-4 py-3"><Badge tone="blue">{entry.action}</Badge></td>
                          <td className="px-4 py-3 text-gray-600">{entry.target_repr || '—'}</td>
                          <td className="px-4 py-3 text-xs text-gray-500 max-w-xs whitespace-normal break-words">
                            {entry.metadata && Object.keys(entry.metadata).length
                              ? Object.entries(entry.metadata).map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`).join(' · ')
                              : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {!activityLoading && (
                <Pagination
                  page={activityPage}
                  count={activityCount}
                  pageSize={ACTIVITY_PAGE_SIZE}
                  onPageChange={setActivityPage}
                />
              )}
            </div>
          </Modal>

          <Modal
            open={!!resetTarget}
            onClose={closePasswordReset}
            title={t('Reset password for {{name}}', {
              name: resetTarget ? `${resetTarget.first_name} ${resetTarget.last_name}` : '',
            })}
            footer={(
              <>
                <Button variant="secondary" onClick={closePasswordReset} disabled={resetBusy}>{t('Cancel')}</Button>
                <Button onClick={submitPasswordReset} loading={resetBusy}>{t('Reset Password')}</Button>
              </>
            )}
          >
            <form onSubmit={submitPasswordReset} className="space-y-4">
              <p className="text-sm text-gray-600">
                {t('Changing the password will end active sessions and prevent existing refresh tokens from being used.')}
              </p>
              {resetErrors._error && <p className="text-sm text-red-600">{resetErrors._error}</p>}
              {resetErrors.detail && <p className="text-sm text-red-600">{resetErrors.detail}</p>}
              <Field label={t('New Password')} error={resetErrors.new_password} required>
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={resetForm.new_password}
                  onChange={(e) => setResetForm((current) => ({ ...current, new_password: e.target.value }))}
                />
              </Field>
              <Field label={t('Confirm Password')} error={resetErrors.password_confirm} required>
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={resetForm.password_confirm}
                  onChange={(e) => setResetForm((current) => ({ ...current, password_confirm: e.target.value }))}
                />
              </Field>
              <Field label={t('Reason')} error={resetErrors.reason} required>
                <Input
                  value={resetForm.reason}
                  onChange={(e) => setResetForm((current) => ({ ...current, reason: e.target.value }))}
                  placeholder={t('Reason (required)...')}
                />
              </Field>
              <button type="submit" className="hidden" aria-hidden="true" />
            </form>
          </Modal>
        </main>
      </div>
    </div>
  );
}

export default AdminUsers;
